/**
 * Add-external-url request validator — SCRUM-147 / DS-011-6. SECURITY-CRITICAL.
 *
 * HTTP-boundary Zod schema for the external-URL POST payload
 * (`POST /organizations/:slug/properties/:propertyId/media/external-url`).
 *
 * The `externalUrl` validation mirrors the `MediaExternalUrl` value object
 * EXACTLY (codex I1 — no deviation). The VO remains the authoritative SSRF
 * gate inside the use case; this schema produces a clean 400 at the wire
 * boundary instead of letting a TypeError surface as a 500.
 *
 * EXACT algorithm (mirror of media-external-url.vo.ts):
 *   1. `new URL(raw)` — reject if it throws (unparseable / no protocol).
 *   2. Require `url.protocol === 'https:'` — reject http://, javascript:,
 *      data:, ftp://, etc.
 *   3. Require `url.username === ''` AND `url.password === ''` — reject userinfo
 *      (userinfo-host-confusion / open-redirect / SSRF class).
 *   4. Reject ANY explicit port. The WHATWG parser strips a scheme's DEFAULT
 *      port, so `new URL('https://host:443/').port === ''`. We additionally
 *      scan the raw authority and reject if it carries an explicit `:port`
 *      (userinfo already rejected in step 3, so any colon there is a port).
 *   5. Accept iff `url.hostname` is an EXACT member of the host allow-set via
 *      `Set.has`. NEVER endsWith / includes / startsWith / regex-suffix —
 *      those are the classic substring-bypass vectors (evilyoutube.com,
 *      youtube.com.evil.com). `new URL` normalises hostname to lowercase.
 *
 * `kind` is 'video-url' | 'matterport-url' (exact enum). Photo uploads use the
 * upload-policy flow, not this endpoint.
 */
import { z } from 'zod';
// EXACT host allow-set — note: www.* variants are NOT members (no prefix match).
// Mirrors ALLOWED_HOSTS in media-external-url.vo.ts.
const ALLOWED_HOSTS = new Set([
    'youtube.com',
    'youtu.be',
    'vimeo.com',
    'matterport.com',
    'my.matterport.com',
]);
/**
 * Detect an explicit `:port` in the authority of an already-parsed,
 * userinfo-free https URL. Needed because the WHATWG parser strips a scheme's
 * default port (`:443`) from `url.port`, so an explicit `:443` would otherwise
 * pass the `url.port === ''` check. The authority is the substring between the
 * `//` and the first `/`, `?`, or `#`. Since userinfo has already been rejected
 * by the caller, any colon in the authority denotes a port.
 *
 * Mirrors hasExplicitPort in media-external-url.vo.ts.
 */
function hasExplicitPort(raw) {
    const schemeSep = raw.indexOf('//');
    if (schemeSep === -1) {
        return false;
    }
    const afterScheme = raw.slice(schemeSep + 2);
    const authorityEnd = afterScheme.search(/[/?#]/);
    const authority = authorityEnd === -1 ? afterScheme : afterScheme.slice(0, authorityEnd);
    return authority.includes(':');
}
/**
 * True iff `raw` satisfies the EXACT MediaExternalUrl algorithm. No
 * endsWith/includes/regex-suffix matching — exact `Set.has` membership only.
 */
function isAllowedExternalUrl(raw) {
    let url;
    try {
        url = new URL(raw);
    }
    catch {
        return false;
    }
    if (url.protocol !== 'https:') {
        return false;
    }
    if (url.username !== '' || url.password !== '') {
        return false;
    }
    // Reject any explicit port (including an explicit default :443).
    if (url.port !== '' || hasExplicitPort(raw)) {
        return false;
    }
    // EXACT membership — never substring / suffix / wildcard matching.
    return ALLOWED_HOSTS.has(url.hostname);
}
export const MEDIA_EXTERNAL_URL_KINDS = ['video-url', 'matterport-url'];
export const addExternalUrlSchema = z.object({
    externalUrl: z
        .string()
        .refine(isAllowedExternalUrl, {
        message: 'externalUrl must be an https URL on an allowed host (youtube.com, youtu.be, vimeo.com, matterport.com, my.matterport.com) with no userinfo and no explicit port',
    }),
    kind: z.enum(MEDIA_EXTERNAL_URL_KINDS),
});
//# sourceMappingURL=add-external-url.schema.js.map